import { createSelector } from 'redux-starter-kit';
import { CustomerType } from 'store/modules/Customers/types';
import {
  AssetAllocationEvalueType,
  PolicyRiskScore,
  TotalOngoingChargeTypeChartData
} from '../Charts/types';
import { PolicyEnumType } from 'utils';
const caseSelector = (state: any) => state.case;

export const caseSelectors = {
  isLoadingCaseInfo: createSelector(
    caseSelector,
    subState => subState.isLoadingCaseInfo
  ),
  isCaseInfoLoaded: createSelector(
    caseSelector,
    subState => subState.isCaseInfoLoaded
  ),
  caseCustomers: createSelector(caseSelector, subState => subState.customers),
  caseCustomer: createSelector(caseSelector, ({ customers }) => {
    const isJointed = customers.length > 1;

    if (isJointed) {
      const jointedCustomer = customers.filter(
        (customer: any) => customer.is_jointed
      );
      return jointedCustomer[0];
    }

    return customers[0];
  }),
  case: createSelector(
    caseSelector,
    ({
      name,
      customers,
      id,
      riskProfiler,
      questionnaireLength,
      riskGroup
    }) => ({
      name,
      customers: customers.filter(
        (customer: CustomerType) => !customer.is_jointed
      ),
      id,
      riskProfiler,
      questionnaireLength,
      riskGroup
    })
  ),
  hasExistingPolicies: createSelector(
    caseSelector,
    subState => subState.existingPolicies.results.length > 0
  ),
  nextPage: createSelector(caseSelector, subState => subState.nextPage),
  getRiskSummary: createSelector(
    caseSelector,
    subState => subState.riskSummaries.results
  ),
  existingPolicies: createSelector(
    caseSelector,
    subState => subState.existingPolicies.results
  ),
  pastPerformance: createSelector(
    caseSelector,
    subState => subState.pastPerformance
  ),
  existingPoliciesPerformance: createSelector(
    caseSelector,
    subState => subState.existingPoliciesPerformance
  ),
  totalPerformanceData: createSelector(
    caseSelector,
    subState => subState.totalPerformanceData
  ),
  portfolioSplit: createSelector(
    caseSelector,
    subState => subState.portfolioSplit
  ),
  existingPoliciesCharges: createSelector(
    caseSelector,
    subState => subState.existingPoliciesCharges
  ),
  recommendedPoliciesCharges: createSelector(
    caseSelector,
    subState => subState.recommendedPoliciesCharges
  ),
  assetAllocations: createSelector(
    caseSelector,
    subState => subState.assetAllocations
  ),
  existingAssetAllocations: createSelector(
    caseSelector,
    subState => subState.existingAssetAllocations
  ),
  sectorBreakdown: createSelector(
    caseSelector,
    subState => subState.sectorBreakdown
  ),
  countryBreakdown: createSelector(
    caseSelector,
    subState => subState.countryBreakdown
  ),
  regionlBreakdown: createSelector(
    caseSelector,
    subState => subState.regionlBreakdown
  ),
  existingSectorBreakdown: createSelector(
    caseSelector,
    subState => subState.existingSectorBreakdown
  ),
  existingCountryBreakdown: createSelector(
    caseSelector,
    subState => subState.existingCountryBreakdown
  ),
  existingRegionlBreakdown: createSelector(
    caseSelector,
    subState => subState.existingRegionlBreakdown
  ),
  totalOngoingCharges: createSelector(caseSelector, subState => {
    const { totalOngoingCharges } = subState;

    const prepareData = totalOngoingCharges.map(
      (policy: TotalOngoingChargeTypeChartData) => {
        return {
          name:
            policy.portfolio === PolicyEnumType.Existing
              ? 'Existing'
              : 'Recommended',
          cash_value: policy.cash_value,
          percentage_value: policy.percentage_value
        };
      }
    );
    return prepareData;
  }),
  portfolioRiskScore: createSelector(caseSelector, subState => {
    const { portfolioRiskScore } = subState;

    const prepareData = portfolioRiskScore.map((policy: PolicyRiskScore) => {
      return {
        name:
          policy.portfolio === PolicyEnumType.Existing
            ? 'Existing'
            : 'Recommended',
        risk_score: policy.risk_score
      };
    });
    return prepareData;
  }),
  existingPolicyRiskScore: createSelector(caseSelector, subState => {
    const { portfolioRiskScore } = subState;
    const filteredRiskScore = portfolioRiskScore.filter(
      (policy: PolicyRiskScore) => policy.portfolio === PolicyEnumType.Existing
    );

    const prepareData = filteredRiskScore.map((policy: PolicyRiskScore) => {
      return {
        name: 'Existing',
        risk_score: policy.risk_score
      };
    });
    return prepareData[0];
  }),
  recopmmendedPolicyRiskScore: createSelector(caseSelector, subState => {
    const { portfolioRiskScore } = subState;
    const filteredRiskScore = portfolioRiskScore.filter(
      (policy: PolicyRiskScore) =>
        policy.portfolio === PolicyEnumType.Recommended
    );

    const prepareData = filteredRiskScore.map((policy: PolicyRiskScore) => {
      return {
        name: 'Recommended',
        risk_score: policy.risk_score
      };
    });
    return prepareData[0];
  }),
  existingPoliciesRiskScore: createSelector(
    caseSelector,
    subState => subState.existingPoliciesRiskScore
  ),
  recommendedPoliciesRiskScore: createSelector(
    caseSelector,
    subState => subState.recommendedPoliciesRiskScore
  ),
  pastPerformanceAggregates: createSelector(caseSelector, subState => {
    return subState.pastPerformanceAggregates;
  }),
  aggregateCostAndCharges: createSelector(
    caseSelector,
    subState => subState.aggregateCostAndCharges
  ),
  aggregateCostAndChargesPolicies: createSelector(
    caseSelector,
    subState => subState.aggregateCostAndChargesPolicies
  ),
  aggregateCostAndChargesPolicyTable: createSelector(
    caseSelector,
    subState => subState.aggregateCostAndChargesPolicyTable
  ),
  assetAllocationEvalue: createSelector(caseSelector, subState =>
    subState.assetAllocationEvalue.map(
      (allocation: AssetAllocationEvalueType) => ({
        name: allocation.name,
        value: Number(allocation.allocation) * 100
      })
    )
  ),
  withdrawals: createSelector(
    caseSelector,
    subState => subState.withdrawals.results
  ),
  policies: createSelector(caseSelector, subState => subState.policies.results),
  contributions: createSelector(
    caseSelector,
    subState => subState.contributions.results
  ),
  contributionSummary: createSelector(
    caseSelector,
    subState => subState.contributionsSummary
  ),
  unallocatedExistingMoney: createSelector(
    caseSelector,
    subState => subState.contributionsSummary.unallocated_existing
  ),
  providers: createSelector(
    caseSelector,
    subState => subState.providers.platforms
  ),
  holdings: createSelector(caseSelector, subState => subState.holdings.results),
  totalOngoingChargeInOneYear: createSelector(caseSelector, subState => {
    const chargeInYearOne = subState.aggregateCostAndChargesPolicyTable[0];
    return {
      totalInitial: chargeInYearOne
        ? chargeInYearOne.total_cumulative_advisor_charge_initial
        : 0,
      totalOngoing: chargeInYearOne
        ? chargeInYearOne.total_cumulative_advisor_charge_ongoing
        : 0
    };
  }),
  advisorCharges: createSelector(caseSelector, subState => subState.results),
  appliedCharges: createSelector(
    caseSelector,
    subState => subState.appliedCharges
  ),
  advisorChargesSummary: createSelector(
    caseSelector,
    subState => subState.advisorChargesSummary
  ),
  allAggregateCostAndCharges: createSelector(
    caseSelector,
    subState => subState.allAggregateCostAndCharges
  ),
  existingRiskAnalysis: createSelector(
    caseSelector,
    subState => subState.existingRiskAnalysis
  ),
  recommendedRiskAnalysis: createSelector(
    caseSelector,
    subState => subState.recommendedRiskAnalysis
  ),
  caseNotes: createSelector(caseSelector, subState => subState.notes)
};
